import { cn } from "@/lib/utils";
import { ChatStore } from "@/stores/ChatStore";
import { EMicStatus } from "@/types/TMicStatus";
import { motion } from "framer-motion";
import { Mic } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type TMicButtonProps = {
  onStart: () => void;
  onStop: () => void;
  onFocusChange?: (focused: boolean) => void;
};

export default function MicButton({
  onStart,
  onStop,
  onFocusChange,
}: TMicButtonProps) {
  const { t } = useTranslation();
  const inputMode = ChatStore(state => state.inputMode);
  const micStatus = ChatStore(state => state.micStatus);
  const isRecording = useMemo(() => micStatus === EMicStatus.recording, [micStatus]);
  const isMicDisabled = useMemo(() => micStatus === EMicStatus.disabled, [micStatus]);
  const isTextMode = useMemo(() => inputMode === "text", [inputMode]);
  return (
    <div className={cn(
      "relative inline-block",
      !isTextMode && "hover:scale-110"
    )}>
      <motion.button
        disabled={isTextMode || isMicDisabled}
        onMouseDown={onStart}
        onMouseUp={onStop}
        onTouchStart={onStart}
        onTouchEnd={onStop}
        onFocus={() => onFocusChange?.(true)}
        onBlur={() => onFocusChange?.(false)}
        className={cn(
          "relative flex h-32 w-32 items-center justify-center rounded-full border border-white shadow-lg",
          isRecording ? "bg-primary-foreground" : isTextMode ? "bg-red-300" : "bg-primary",
          "text-white shadow-lg transition-colors duration-200 ease-in-out"
        )}
        animate={isRecording ? { scale: [1, 0.8, 0.9, 0.8, 1] } : { scale: 1 }}
        transition={isRecording && !isTextMode ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : undefined}
        aria-label={t('common.pushToTalk')}
        title={t('common.pushToTalkHint')}
        autoFocus
      >
        <Mic className="relative h-14 w-14" strokeWidth={2} />
      </motion.button>
    </div>
  );
}